// File: src/pages/bluenext/BlueNextFAQ.jsx
import React, { useState } from "react";

const faqData = {
  "Workers / Job Seekers": [
    {
      sentiment: "negative",
      question: "I don’t have high education — can I still use BlueNext?",
      answer:
        "Yes! BlueNext is made especially for skilled and semi-skilled workers. You can create your profile, showcase your experience, and apply for suitable jobs locally or abroad.",
    },
    {
      sentiment: "negative",
      question: "I don’t know English or Hindi — can I still use this?",
      answer:
        "Absolutely. The platform supports local languages and has icon + voice-based navigation, making it easy for everyone to use.",
    },
    {
      sentiment: "neutral",
      question: "Is BlueNext free for job seekers?",
      answer:
        "Yes, creating your BlueNext profile and applying for jobs is completely free. Premium services like visa support or training are optional.",
    },
    {
      sentiment: "positive",
      question: "Can I find overseas jobs through BlueNext?",
      answer:
        "Yes! Through the Global Jobs for Indians module, you can explore verified opportunities in GCC countries, Europe, and other regions with trusted consultants like WorkBridge.",
    },
    {
      sentiment: "positive",
      question: "Can I learn new skills here?",
      answer:
        "Yes. Visit the ‘Skill Development’ and ‘Digital Learning & Income’ sections to access free training, workshops, and certifications to boost your career.",
    },
  ],

  "Employers / Contractors": [
    {
      sentiment: "negative",
      question: "How can I trust worker profiles on BlueNext?",
      answer:
        "All BlueNext users are identity-verified with digital Blue Cards. You can also view their skills, certificates, attendance history, and feedback before hiring.",
    },
    {
      sentiment: "neutral",
      question: "Can I hire both locally and overseas?",
      answer:
        "Yes. You can post jobs for Indian locations or request overseas hiring support through our global partner network.",
    },
    {
      sentiment: "positive",
      question: "Does BlueNext offer workforce management tools?",
      answer:
        "Yes! The ‘Task & Management’ section helps track attendance, productivity, and progress — digitally and securely.",
    },
  ],

  "Skill Trainers / Institutions": [
    {
      sentiment: "negative",
      question: "We already run training programs — why use BlueNext?",
      answer:
        "BlueNext helps digitize your programs, issue digital certificates, and connect your trained candidates with verified employers instantly.",
    },
    {
      sentiment: "neutral",
      question: "Can I host digital workshops or podcasts?",
      answer:
        "Yes, through the ‘Skill Development’ module you can host online sessions, publish podcasts, or list workshops for learners across India.",
    },
    {
      sentiment: "positive",
      question: "Do trainers get visibility or recognition?",
      answer:
        "Yes. Certified trainers appear in the Skill Directory and get badges based on ratings, impact, and verified training hours.",
    },
  ],

  "Global Job Consultants": [
    {
      sentiment: "negative",
      question: "How do I join the BlueNext global consultant network?",
      answer:
        "You can register through the ‘WorkBridge’ section under Global Jobs. Once verified, you can list jobs, manage applicants, and provide visa support.",
    },
    {
      sentiment: "neutral",
      question: "What kind of jobs are in demand overseas?",
      answer:
        "Skilled trades, construction, drivers, hospitality, oil & gas, and healthcare workers are currently in high demand across GCC and parts of Europe.",
    },
    {
      sentiment: "positive",
      question: "Does BlueNext support visa processing and travel?",
      answer:
        "Yes. WorkBridge partners offer end-to-end assistance for visa, travel, and accommodation to ensure smooth onboarding.",
    },
  ],

  "Government / Policy Makers": [
    {
      sentiment: "negative",
      question: "Will rural or informal workers be able to use this?",
      answer:
        "Yes. BlueNext is mobile-first, voice-guided, and works on low-data phones — ideal for rural users and small-town workers.",
    },
    {
      sentiment: "neutral",
      question: "Can BlueNext support government skilling or employment schemes?",
      answer:
        "Yes. The platform integrates with skilling missions like Skill India, PMKVY, and state livelihood programs to enhance outreach and monitoring.",
    },
    {
      sentiment: "positive",
      question: "Can BlueNext data help in workforce planning?",
      answer:
        "Aggregated workforce analytics can guide policy decisions in training, migration, and employment at district, state, and national levels.",
    },
  ],
};

// Sentiment color mapping
const sentimentColors = {
  negative: "text-red-400",
  neutral: "text-gray-400",
  positive: "text-emerald-400",
};

export default function BlueNextFAQ() {
  const stakeholders = Object.keys(faqData);
  const [selectedStakeholder, setSelectedStakeholder] = useState(stakeholders[0]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-8">
      <h1 className="text-lg sm:text-xl font-bold mb-6 text-center text-emerald-400">
        🧢 BlueNext – FAQ
      </h1>
      <p className="text-center text-gray-300 mb-8 text-sm sm:text-base">
        Extending Digital Empowerment for Blue Collar Literacy, Productivity, and Growth
      </p>

      {/* Stakeholder Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-6">
        {stakeholders.map((stakeholder) => (
          <button
            key={stakeholder}
            onClick={() => {
              setSelectedStakeholder(stakeholder);
              setExpandedIndex(null);
            }}
            className={`px-4 py-2 rounded-md font-semibold text-sm sm:text-base transition-all duration-200 ${
              selectedStakeholder === stakeholder
                ? "bg-emerald-500 text-white"
                : "bg-gray-800 hover:bg-gray-700"
            }`}
          >
            {stakeholder}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <div className="max-w-4xl mx-auto">
        {faqData[selectedStakeholder].map((faq, index) => (
          <div
            key={index}
            className="border-b border-gray-700 py-3 cursor-pointer"
            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
          >
            <div className="flex justify-between items-center">
              <h3 className={`font-medium ${sentimentColors[faq.sentiment]}`}>
                {faq.question}
              </h3>
              <span className="text-gray-400 text-xl">
                {expandedIndex === index ? "-" : "+"}
              </span>
            </div>
            {expandedIndex === index && (
              <p className="mt-2 text-gray-300">{faq.answer}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
